import React, { useEffect, useMemo, useState } from "react";
import Cookies from "js-cookie";
import Sidebar from "../../components/Sidebar";
import Button from "../../components/Buttons/Button";
import ConfirmModal from "../../components/Modals/ConfirmModal/ConfirmModal";
import { downloadIcon as DownloadIcon, eyeClosed as EyeClosedIcon, eyeIcon as EyeIcon } from "assets/icons";
import { useUser } from "../../context/UserContext";
import {
  createBotConfig,
  deleteBotConfig,
  getBotConfig,
  getUserData,
  restartBot,
  updateBotConfig,
  updateUserData,
} from "../../services/settingsService/settingsService";
import { sendVerificationCode } from "../../services/authService/authService";
import { useNotify } from "context/NotificationContext";
import { logger } from "utils/logger";
import { getServerErrorData } from "utils/httpError";
import { BotFormData, UserFormData } from "./SettingsPage.types";
import TeacherCatalogLinkSection from "./TeacherCatalogLinkSection";
import {
  BOT_TOKEN_RE,
  EMAIL_RE,
  botErrorKeys,
  normalizeErrors,
  sameBot,
  sameUserProfile,
  sanitizeTechSupportName,
  sanitizeVerificationCode,
  userErrorKeys,
} from "./settings.utils";
import {
  ActionGroup,
  CodeHint,
  ContentInner,
  Control,
  ErrorSummary,
  Hint,
  Input,
  InstructionHint,
  InstructionLink,
  LoadingState,
  NameFields,
  PageContent,
  PageLead,
  PageTitle,
  PeekButton,
  Prefix,
  ResendButton,
  RowLabel,
  SectionAside,
  SectionBody,
  SettingsPageContainer,
  SettingsRow,
  SettingsSection,
  StatusDot,
  StatusLine,
  StatusNote,
} from "./SettingsPage.styles";

type BusyAction = "profile-code" | "profile-save" | "bot-save" | "bot-restart" | "bot-delete" | null;

const emptyUser: UserFormData = {
  organization: "",
  firstName: "",
  lastName: "",
  email: "",
  verificationCode: "",
};

const emptyBot: BotFormData = {
  botToken: "",
  techSupportTGname: "",
};

const SettingsPage: React.FC = () => {
  const { reloadUser } = useUser();
  const { show: showToast } = useNotify();
  const userId = Number(Cookies.get("user_id"));

  const [savedUser, setSavedUser] = useState<UserFormData>(emptyUser);
  const [userDraft, setUserDraft] = useState<UserFormData>(emptyUser);
  const [savedBot, setSavedBot] = useState<BotFormData>(emptyBot);
  const [botDraft, setBotDraft] = useState<BotFormData>(emptyBot);
  const [botConfigId, setBotConfigId] = useState<number | null>(null);
  const [isVerificationCodeSent, setIsVerificationCodeSent] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userErrors, setUserErrors] = useState<Record<string, string>>({});
  const [botErrors, setBotErrors] = useState<Record<string, string>>({});
  const [busyAction, setBusyAction] = useState<BusyAction>(null);
  const [initialLoading, setInitialLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      logger.error("ID пользователя отсутствует");
      showToast({
        severity: "error",
        summary: "Ошибка",
        detail: "ID пользователя отсутствует",
        life: 3000,
      });
      setInitialLoading(false);
      return;
    }

    let active = true;

    const loadSettings = async () => {
      try {
        const [userData, botConfigs] = await Promise.all([
          getUserData(userId),
          getBotConfig(userId),
        ]);
        if (!active) return;

        const nextUser: UserFormData = {
          organization: userData.organization_name || "",
          firstName: userData.first_name || "",
          lastName: userData.last_name || "",
          email: userData.email || "",
          verificationCode: "",
        };
        setSavedUser(nextUser);
        setUserDraft(nextUser);

        if (botConfigs.length > 0) {
          const config = botConfigs[0];
          const nextBot: BotFormData = {
            botToken: config.bot_token || "",
            techSupportTGname: config.tech_support_tg_name || "",
          };
          setBotConfigId(config.id);
          setSavedBot(nextBot);
          setBotDraft(nextBot);
        }
      } catch (error) {
        logger.error("Ошибка при загрузке настроек:", error);
        showToast({
          severity: "error",
          summary: "Ошибка",
          detail: "Не удалось загрузить настройки",
          life: 3000,
        });
      } finally {
        if (active) setInitialLoading(false);
      }
    };

    loadSettings();
    return () => {
      active = false;
    };
  }, [showToast, userId]);

  const emailChanged = userDraft.email.trim() !== savedUser.email.trim();
  const profileDirty = !sameUserProfile(savedUser, userDraft);
  const botDirty = !sameBot(savedBot, botDraft);
  const botConnected = botConfigId !== null;
  const isBusy = busyAction !== null;
  const emailValid = EMAIL_RE.test(userDraft.email.trim());
  const profileFilled = [
    userDraft.organization,
    userDraft.firstName,
    userDraft.lastName,
    userDraft.email,
  ].every((value) => value.trim().length > 0);

  const profileCanSubmit =
    profileDirty &&
    profileFilled &&
    emailValid &&
    !isBusy &&
    (!emailChanged || !isVerificationCodeSent || userDraft.verificationCode.trim().length === 6);

  const botFilled = botDraft.botToken.trim().length > 0 && botDraft.techSupportTGname.trim().length > 0;
  const botCanSubmit = botDirty && botFilled && !isBusy;

  const profileActionLabel = emailChanged && !isVerificationCodeSent ? "Отправить код" : "Сохранить";
  const botActionLabel = botConnected ? "Сохранить" : "Создать бота";

  const profileErrorSummary = useMemo(
    () => userErrors.non_field_errors || userErrors.detail || userErrors.error || "",
    [userErrors],
  );
  const botErrorSummary = useMemo(
    () => botErrors.non_field_errors || botErrors.detail || botErrors.error || "",
    [botErrors],
  );

  const clearUserError = (field: keyof UserFormData) => {
    setUserErrors((current) => {
      if (!current[field]) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
  };

  const clearBotError = (field: keyof BotFormData) => {
    setBotErrors((current) => {
      if (!current[field]) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
  };

  const handleUserChange = (field: keyof UserFormData, value: string) => {
    clearUserError(field);
    setUserDraft((current) => {
      if (field === "email") {
        setIsVerificationCodeSent(false);
        return { ...current, email: value, verificationCode: "" };
      }
      if (field === "verificationCode") {
        return { ...current, verificationCode: sanitizeVerificationCode(value) };
      }
      return { ...current, [field]: value };
    });
  };

  const handleProfileCancel = () => {
    setUserDraft({ ...savedUser, verificationCode: "" });
    setIsVerificationCodeSent(false);
    setUserErrors({});
  };

  const sendEmailCode = async (resend = false) => {
    setBusyAction("profile-code");
    try {
      await sendVerificationCode(userDraft.email.trim(), "emailChange");
      setIsVerificationCodeSent(true);
      showToast({
        severity: "success",
        summary: "Успешно",
        detail: resend ? "Код отправлен ещё раз" : "Код подтверждения отправлен",
        life: 3000,
      });
    } catch (error) {
      const serverErrors = getServerErrorData(error);
      if (serverErrors) {
        setUserErrors(normalizeErrors(serverErrors, userErrorKeys));
      } else {
        logger.error("Ошибка при отправке кода подтверждения:", error);
        showToast({
          severity: "error",
          summary: "Ошибка",
          detail: "Не удалось отправить код подтверждения",
          life: 3000,
        });
      }
    } finally {
      setBusyAction(null);
    }
  };

  const handleProfileSubmit = async () => {
    if (!profileCanSubmit) return;

    if (emailChanged && !isVerificationCodeSent) {
      await sendEmailCode(false);
      return;
    }

    setBusyAction("profile-save");
    try {
      const updateData: Record<string, string> = {
        organization_name: userDraft.organization.trim(),
        first_name: userDraft.firstName.trim(),
        last_name: userDraft.lastName.trim(),
      };

      if (emailChanged) {
        updateData.email = userDraft.email.trim();
        updateData.verification_code = userDraft.verificationCode.trim();
      }

      await updateUserData(userId, updateData);

      Cookies.set("first_name", userDraft.firstName.trim());
      Cookies.set("last_name", userDraft.lastName.trim());
      if (emailChanged) Cookies.set("email", userDraft.email.trim());
      await reloadUser();

      const nextSaved: UserFormData = {
        organization: userDraft.organization.trim(),
        firstName: userDraft.firstName.trim(),
        lastName: userDraft.lastName.trim(),
        email: userDraft.email.trim(),
        verificationCode: "",
      };
      setSavedUser(nextSaved);
      setUserDraft(nextSaved);
      setIsVerificationCodeSent(false);
      setUserErrors({});
      showToast({
        severity: "success",
        summary: "Успешно",
        detail: "Данные сохранены",
        life: 3000,
      });
    } catch (error) {
      const serverErrors = getServerErrorData(error);
      if (serverErrors) {
        setUserErrors(normalizeErrors(serverErrors, userErrorKeys));
      } else {
        logger.error("Ошибка обновления данных пользователя:", error);
        showToast({
          severity: "error",
          summary: "Ошибка",
          detail: "Не удалось сохранить данные",
          life: 3000,
        });
      }
    } finally {
      setBusyAction(null);
    }
  };

  const handleBotChange = (field: keyof BotFormData, value: string) => {
    clearBotError(field);
    const processedValue = field === "techSupportTGname" ? sanitizeTechSupportName(value) : value;
    setBotDraft((current) => ({ ...current, [field]: processedValue }));
  };

  const handleBotCancel = () => {
    setBotDraft(savedBot);
    setBotErrors({});
  };

  const handleBotSubmit = async () => {
    if (!botCanSubmit) return;

    if (!BOT_TOKEN_RE.test(botDraft.botToken.trim())) {
      setBotErrors((current) => ({
        ...current,
        botToken: "Токен выглядит неверно. Проверьте значение, полученное у BotFather.",
      }));
      return;
    }

    setBusyAction("bot-save");
    const creating = !botConnected;
    showToast({
      severity: "info",
      summary: creating ? "Создание" : "Сохранение",
      detail: creating
        ? "Создаём Телеграм-бота. Операция может занять до минуты"
        : "Сохраняем настройки бота",
      life: 6000,
    });

    try {
      if (botConfigId) {
        await updateBotConfig(botConfigId, userId, {
          bot_token: botDraft.botToken.trim(),
          tech_support_tg_name: botDraft.techSupportTGname.trim(),
        });
      } else {
        const created = await createBotConfig({
          user: userId,
          bot_token: botDraft.botToken.trim(),
          tech_support_tg_name: botDraft.techSupportTGname.trim(),
        });
        setBotConfigId(created.id);
      }

      const nextBot = {
        botToken: botDraft.botToken.trim(),
        techSupportTGname: botDraft.techSupportTGname.trim(),
      };
      setSavedBot(nextBot);
      setBotDraft(nextBot);
      setBotErrors({});
      showToast({
        severity: "success",
        summary: "Успешно",
        detail: creating ? "Бот создан" : "Настройки бота сохранены",
        life: 3000,
      });
    } catch (error) {
      const serverErrors = getServerErrorData(error);
      if (serverErrors) {
        setBotErrors(normalizeErrors(serverErrors, botErrorKeys));
      } else {
        logger.error("Ошибка сохранения настроек бота:", error);
        showToast({
          severity: "error",
          summary: "Ошибка",
          detail: "Не удалось сохранить настройки бота",
          life: 3000,
        });
      }
    } finally {
      setBusyAction(null);
    }
  };

  const handleBotRestart = async () => {
    if (!botConfigId || isBusy) return;
    setBusyAction("bot-restart");
    showToast({
      severity: "info",
      summary: "Перезапуск",
      detail: "Перезапускаем бота. Операция может занять до минуты",
      life: 6000,
    });
    try {
      await restartBot(botConfigId);
      showToast({ severity: "success", summary: "Успешно", detail: "Бот перезапущен", life: 3000 });
    } catch (error) {
      logger.error("Ошибка при перезапуске бота:", error);
      showToast({ severity: "error", summary: "Ошибка", detail: "Не удалось перезапустить бота", life: 3000 });
    } finally {
      setBusyAction(null);
    }
  };

  const handleBotDelete = async () => {
    if (!botConfigId || isBusy) return;
    setShowDeleteConfirm(false);
    setBusyAction("bot-delete");
    try {
      await deleteBotConfig(botConfigId);
      setBotConfigId(null);
      setSavedBot(emptyBot);
      setBotDraft(emptyBot);
      setBotErrors({});
      setShowToken(false);
      showToast({ severity: "success", summary: "Успешно", detail: "Бот удалён", life: 3000 });
    } catch (error) {
      logger.error("Ошибка при удалении бота:", error);
      showToast({ severity: "error", summary: "Ошибка", detail: "Не удалось удалить бота", life: 3000 });
    } finally {
      setBusyAction(null);
    }
  };

  return (
    <SettingsPageContainer>
      <Sidebar />
      <PageContent>
        <ContentInner>
          <PageTitle>Настройки</PageTitle>
          <PageLead>Данные организации и подключение Телеграм-бота.</PageLead>

          {initialLoading ? (
            <LoadingState>Загружаем настройки…</LoadingState>
          ) : (
            <>
              <SettingsSection>
                <SectionAside>
                  <h2>Организация и профиль</h2>
                  <p>Имя и фамилия видны ученикам в переписке, почта используется для входа.</p>
                </SectionAside>

                <SectionBody>
                  <SettingsRow>
                    <RowLabel>Организация</RowLabel>
                    <div>
                      <Control>
                        <Input
                          aria-label="Организация"
                          value={userDraft.organization}
                          $error={Boolean(userErrors.organization)}
                          onChange={(event) => handleUserChange("organization", event.target.value)}
                          disabled={isBusy}
                        />
                      </Control>
                      {userErrors.organization && <Hint $error>{userErrors.organization}</Hint>}
                    </div>
                  </SettingsRow>

                  <SettingsRow>
                    <RowLabel>Имя и фамилия</RowLabel>
                    <div>
                      <NameFields>
                        <Input
                          aria-label="Имя"
                          value={userDraft.firstName}
                          $error={Boolean(userErrors.firstName)}
                          onChange={(event) => handleUserChange("firstName", event.target.value)}
                          disabled={isBusy}
                        />
                        <Input
                          aria-label="Фамилия"
                          value={userDraft.lastName}
                          $error={Boolean(userErrors.lastName)}
                          onChange={(event) => handleUserChange("lastName", event.target.value)}
                          disabled={isBusy}
                        />
                      </NameFields>
                      {userErrors.firstName && <Hint $error>{userErrors.firstName}</Hint>}
                      {userErrors.lastName && <Hint $error>{userErrors.lastName}</Hint>}
                    </div>
                  </SettingsRow>

                  <SettingsRow>
                    <RowLabel>Электронная почта</RowLabel>
                    <div>
                      <Control>
                        <Input
                          aria-label="Электронная почта"
                          type="email"
                          value={userDraft.email}
                          $error={Boolean(userErrors.email) || (Boolean(userDraft.email) && !emailValid)}
                          onChange={(event) => handleUserChange("email", event.target.value)}
                          disabled={isBusy}
                        />
                      </Control>
                      {userErrors.email ? (
                        <Hint $error>{userErrors.email}</Hint>
                      ) : userDraft.email && !emailValid ? (
                        <Hint $error>Неверный адрес электронной почты</Hint>
                      ) : emailChanged && !isVerificationCodeSent ? (
                        <Hint $brand>Чтобы подтвердить новый адрес, отправим на него код.</Hint>
                      ) : null}
                    </div>
                  </SettingsRow>

                  {emailChanged && isVerificationCodeSent && (
                    <SettingsRow>
                      <RowLabel>Код из письма</RowLabel>
                      <div>
                        <Control>
                          <Input
                            aria-label="Код из письма"
                            inputMode="numeric"
                            maxLength={6}
                            placeholder="000000"
                            value={userDraft.verificationCode}
                            $error={Boolean(userErrors.verificationCode)}
                            onChange={(event) => handleUserChange("verificationCode", event.target.value)}
                            disabled={isBusy}
                          />
                        </Control>
                        {userErrors.verificationCode ? (
                          <Hint $error>{userErrors.verificationCode}</Hint>
                        ) : (
                          <CodeHint>
                            Отправили на {userDraft.email}.
                            <ResendButton
                              type="button"
                              onClick={() => sendEmailCode(true)}
                              disabled={isBusy}
                            >
                              Отправить ещё раз
                            </ResendButton>
                          </CodeHint>
                        )}
                      </div>
                    </SettingsRow>
                  )}

                  <SettingsRow>
                    <RowLabel $tight />
                    <div>
                      <ActionGroup>
                        <Button
                          variant="primary"
                          onClick={handleProfileSubmit}
                          disabled={!profileCanSubmit}
                          loading={busyAction === "profile-code" || busyAction === "profile-save"}
                        >
                          {profileActionLabel}
                        </Button>
                        {profileDirty && (
                          <Button variant="secondary" onClick={handleProfileCancel} disabled={isBusy}>
                            Отменить
                          </Button>
                        )}
                      </ActionGroup>
                      {profileErrorSummary && <ErrorSummary>{profileErrorSummary}</ErrorSummary>}
                    </div>
                  </SettingsRow>
                </SectionBody>
              </SettingsSection>

              <TeacherCatalogLinkSection />

              <SettingsSection>
                <SectionAside>
                  <h2>Телеграм-бот</h2>
                  <p>Через бота ученики получают уроки и сдают домашние задания.</p>
                </SectionAside>

                <SectionBody>
                  <SettingsRow>
                    <RowLabel $tight>Состояние</RowLabel>
                    <div>
                      <StatusLine>
                        <StatusDot $connected={botConnected} />
                        <span>{botConnected ? "Бот подключён" : "Бот не подключён"}</span>
                      </StatusLine>
                      <StatusNote>
                        {botConnected
                          ? "Ученики получают уроки и отправляют домашние задания через бота."
                          : "Заполните токен и имя техподдержки, чтобы создать бота."}
                      </StatusNote>
                    </div>
                  </SettingsRow>

                  <SettingsRow>
                    <RowLabel>Токен бота</RowLabel>
                    <div>
                      <Control>
                        <Input
                          aria-label="Токен бота"
                          type={showToken ? "text" : "password"}
                          placeholder="0000000000:AA..."
                          value={botDraft.botToken}
                          $hasAction
                          $error={Boolean(botErrors.botToken)}
                          onChange={(event) => handleBotChange("botToken", event.target.value)}
                          disabled={isBusy}
                        />
                        <PeekButton
                          type="button"
                          aria-label={showToken ? "Скрыть токен" : "Показать токен"}
                          onClick={() => setShowToken((current) => !current)}
                        >
                          {showToken ? <EyeClosedIcon aria-hidden="true" /> : <EyeIcon aria-hidden="true" />}
                        </PeekButton>
                      </Control>
                      {botErrors.botToken ? (
                        <Hint $error>{botErrors.botToken}</Hint>
                      ) : (
                        <InstructionHint>
                          Токен выдаёт BotFather при создании бота.
                          <InstructionLink href="/bot_instruction01.pdf" download>
                            <DownloadIcon aria-hidden="true" />
                            инструкция
                          </InstructionLink>
                        </InstructionHint>
                      )}
                    </div>
                  </SettingsRow>

                  <SettingsRow>
                    <RowLabel>Техподдержка</RowLabel>
                    <div>
                      <Control>
                        <Prefix>@</Prefix>
                        <Input
                          aria-label="Телеграм техподдержки"
                          placeholder="support_school"
                          value={botDraft.techSupportTGname}
                          $hasPrefix
                          $error={Boolean(botErrors.techSupportTGname)}
                          onChange={(event) => handleBotChange("techSupportTGname", event.target.value)}
                          disabled={isBusy}
                        />
                      </Control>
                      {botErrors.techSupportTGname ? (
                        <Hint $error>{botErrors.techSupportTGname}</Hint>
                      ) : (
                        <Hint>Ученики будут писать сюда, если что-то сломалось.</Hint>
                      )}
                    </div>
                  </SettingsRow>

                  <SettingsRow>
                    <RowLabel $tight>Управление</RowLabel>
                    <div>
                      <ActionGroup>
                        <Button
                          variant="primary"
                          onClick={handleBotSubmit}
                          disabled={!botCanSubmit}
                          loading={busyAction === "bot-save"}
                        >
                          {botActionLabel}
                        </Button>
                        {botDirty && (
                          <Button variant="secondary" onClick={handleBotCancel} disabled={isBusy}>
                            Отменить
                          </Button>
                        )}
                        <Button
                          variant="secondary"
                          onClick={handleBotRestart}
                          disabled={!botConnected || isBusy}
                          loading={busyAction === "bot-restart"}
                        >
                          Перезапустить
                        </Button>
                        <Button
                          variant="dangerSecondary"
                          onClick={() => setShowDeleteConfirm(true)}
                          disabled={!botConnected || isBusy}
                        >
                          Удалить
                        </Button>
                      </ActionGroup>
                      <Hint>
                        {botConnected
                          ? "Изменения применяются сразу. Перезапуск может занять до минуты."
                          : "Заполните поля выше, чтобы создать бота. Операция может занять до минуты."}
                      </Hint>
                      {botErrorSummary && <ErrorSummary>{botErrorSummary}</ErrorSummary>}
                    </div>
                  </SettingsRow>
                </SectionBody>
              </SettingsSection>
            </>
          )}
        </ContentInner>
      </PageContent>

      <ConfirmModal
        isOpen={showDeleteConfirm}
        title="Удалить бота"
        message="Ученики перестанут получать сообщения, токен придётся вводить заново."
        onConfirm={handleBotDelete}
        onCancel={() => setShowDeleteConfirm(false)}
        confirmButtonText="Удалить"
        confirmVariant="danger"
      />
    </SettingsPageContainer>
  );
};

export default SettingsPage;
